import { Directive, inject, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { ROLES_KEYS, SupportingCompanyAccessLevel } from '@interfaces';
import { UserAuthFacade } from '@state/user-auth';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';

interface RequiredSupportingCompanyDirectiveOpts {
  companyId: string;
  requiredAccessLevel: SupportingCompanyAccessLevel;
}

@Directive({
  selector: '[appRequiredSupportingCompany]',
  standalone: false,
})
export class RequiredSupportingCompanyDirective {
  private readonly templateRef = inject(TemplateRef<any>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly userAuthFacade = inject(UserAuthFacade);
  private hasView = false;
  private renderRequestId = 0;

  @Input() set appRequiredSupportingCompany(opts: RequiredSupportingCompanyDirectiveOpts | null | undefined) {
    const renderRequestId = ++this.renderRequestId;

    if (!opts?.companyId || !opts?.requiredAccessLevel) {
      this.showTemplate();
      return;
    }

    this.renderContainerIfConditionsMet(opts, renderRequestId);
  }

  private async renderContainerIfConditionsMet(
    { requiredAccessLevel, companyId }: RequiredSupportingCompanyDirectiveOpts,
    renderRequestId: number
  ): Promise<boolean> {
    try {
      const myself = await firstValueFrom(
        this.userAuthFacade.myself$.pipe(
          filter(myself => !!myself?.id),
          take(1)
        )
      );

      if (renderRequestId !== this.renderRequestId) {
        return false;
      }

      if (myself?.role?.key === ROLES_KEYS.Admin) {
        this.showTemplate();
        return true;
      }

      if (!myself?.supportingCompanies?.length) {
        console.error(`Employee has no 'supportingCompanies' array populated`);
        this.hideTemplate();
        return false;
      }

      const hasRequiredAccess = myself.supportingCompanies.find(
        sc => sc[requiredAccessLevel] && sc.companyId === companyId
      );
      if (!hasRequiredAccess) {
        this.hideTemplate();
        return false;
      }

      this.showTemplate();
      return true;
    } catch (error) {
      if (renderRequestId !== this.renderRequestId) {
        return false;
      }

      console.error(`Error reading 'supportingCompanies' array from myself: `, error);
      this.hideTemplate();
      return false;
    }
  }

  private showTemplate(): void {
    if (this.hasView) {
      return;
    }

    this.viewContainer.createEmbeddedView(this.templateRef);
    this.hasView = true;
  }

  private hideTemplate(): void {
    if (!this.hasView) {
      return;
    }

    this.viewContainer.clear();
    this.hasView = false;
  }
}
